import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css'; // Core styles
import 'ag-grid-community/styles/ag-theme-balham.css'; // Optional theme
import './ag-theme-ela.css';

import * as CellRenderers from './AgGridCellRendererComponents';

const moment = require('moment');

const DEFAULT_MIN_WIDTH_CELL = 10;
const DEFAULT_MAX_WIDTH_CELL = 800;

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  minWidth: DEFAULT_MIN_WIDTH_CELL,
  maxWidth: DEFAULT_MAX_WIDTH_CELL,
  cellStyle: {
    borderRight: '1px solid #ccc', // Adds a light gray divider
  },
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// helper (optional)
const formatUSD = ({ value }) =>
  value == null || value === '' ? '' : usd.format(Number(value));

const columns = [
  {
    headerName: '',
    width: 30,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    sortable: false,
    filter: false,
  },
  {
    headerName: '',
    field: 'repEmail2',
    cellRenderer: 'repEmail2CellRenderer',
    width: 30,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Job#',
    field: 'jobNum',
    cellRenderer: 'jobNumCellRenderer',
    width: 65,
  },
  {
    headerName: 'Audit',
    field: 'auditFlg',
    cellRenderer: 'auditFlgCellRenderer',
    width: 65,
  },
  {
    headerName: '',
    field: 'transportationFlg',
    cellRenderer: 'transportationCellRenderer',
    width: 20,
    textAlign: 'center',
  },
  {
    headerName: 'Date',
    field: 'jobDateTime',
    cellRenderer: 'jobDateCellRenderer',
    tooltipValueGetter: function (params) {
      const { data } = params;
      const jobDate = data.jobDateTime
        ? moment(data.jobDateTime).format('MM/DD/YYYY hh:mm A')
        : '';
      const finishTime = data.finishTimeString
        ? ' to ' + data.finishTimeString
        : '';
      const tooltipContent = `CreatedBy: ${
        data.createdBy
      }\nCreated On: ${moment(data.createDateTime).format(
        'MM/DD/YYYY hh:mm A'
      )}\nUpdated By: ${data.updatedBy}\nUpdated On: ${moment(
        data.updateDateTime
      ).format('MM/DD/YYYY hh:mm A')}`;
      return `${jobDate} ${finishTime}\n${tooltipContent}`;
    },
    width: 125,
  },
  {
    headerName: 'Dur',
    field: 'duration',
    cellRenderer: 'durationCellRenderer',
    width: 55,
  },
  {
    headerName: 'Claimant',
    field: 'clmLastName',
    cellRenderer: 'claimantNameCellRenderer',
    tooltipValueGetter: function (params) {
      const { data } = params;
      const claimantNotes = data.clmNote === null ? '' : data.clmNote;
      const tooltipContent = `${claimantNotes}`;
      return tooltipContent;
    },
    width: 125,
  },
  { headerName: 'CLM#', field: 'clmClaimNumber', width: 100 },
  { headerName: 'Client', field: 'clientAbbrv', width: 68 },
  { headerName: 'Disc', field: 'jobDiscount', width: 60 },
  { headerName: 'Overtime',  field: 'intOTFee',   width: 60, valueFormatter: formatUSD, type: 'numericColumn' },
  { headerName: 'Other Fee', field: 'intFeeOther', width: 60, valueFormatter: formatUSD, type: 'numericColumn'  },
  { headerName: 'Int Fee', field: 'intFee', width: 60, valueFormatter: formatUSD, type: 'numericColumn'  },
  { headerName: 'Total', field: 'jobTotal', width: 70, valueFormatter: formatUSD, type: 'numericColumn' },
  {
    headerName: 'Interpreter',
    field: 'intLastName',
    cellRenderer: 'intNameCellRenderer',
    width: 120,
  },
  { headerName: 'Caller Notes', field: 'callerNote', width: 120 },
  {
    headerName: 'Rep',
    field: 'repLastName',
    cellRenderer: 'repNameCellRenderer',
    width: 120,
  },
  { headerName: 'Invoice Notes', field: 'invoiceNote' },
];

export class WorkOrderTableAudit extends Component {
  static displayName = WorkOrderTableAudit.name;

  constructor(props) {
    super(props);
    this.state = {
      columnWidths: columns.reduce((acc, col) => {
        acc[col.field] = col.width || DEFAULT_MIN_WIDTH_CELL; // Initialize widths
        return acc;
      }, {}),
      isResizing: -1,
    };
    this.gridApi = null;
    this.gridColumnApi = null;
    this.previousSelection = [];

    // Bind methods
    this.onColumnResized = this.onColumnResized.bind(this);
    this.autoSizeUnspecifiedColumns =
      this.autoSizeUnspecifiedColumns.bind(this);
    this.adjustHeaderHeight = this.adjustHeaderHeight.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.onRowClicked = this.onRowClicked.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onDataLoaded = this.onDataLoaded.bind(this);
    this.onSortChanged = this.onSortChanged.bind(this);
  }

  componentDidMount() {
    ReactTooltip.rebuild();
  }

  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.workOrders !== this.props.workOrders && this.gridApi) {
      return this.gridApi.getFilterModel();
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.clearSelection && !prevProps.clearSelection) {
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
    }
    if (snapshot !== null && this.gridApi) {
      this.gridApi.setFilterModel(snapshot);
    }
  }

  autoSizeUnspecifiedColumns = () => {
    const allColumnIds = [];
    this.gridColumnApi.getColumns().forEach((column) => {
      // Check if the width is not set in the column definition
      if (column.getColDef().width === undefined) {
        allColumnIds.push(column.colId);
      }
    });
    if (allColumnIds.length > 0) {
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    }
  };
  //
  adjustHeaderHeight = (gridApi) => {
    window.requestAnimationFrame(() => {
      const allColumnHeaders = document.querySelectorAll(
        '.ag-header-cell-text'
      );
      let maxHeight = 0;
      allColumnHeaders.forEach((header) => {
        if (header.parentElement.clientHeight > maxHeight) {
          maxHeight = header.parentElement.clientHeight;
        }
      });
      maxHeight += 10;
      gridApi.setHeaderHeight(maxHeight);
    });
  };

  handleRowDoubleClick = (event) => {
    const workorder = event.data;
    const rowIndex = event.rowIndex;
    this.props.setData(workorder, rowIndex, true);
  };

  onRowClicked = (event) => {};

  onSelectionChanged = () => {
    const currentSelection = this.gridApi.getSelectedRows();
    const currentSelectionIds = currentSelection.map((item) => item.jobId);
    const previousSelectionIds = this.previousSelection.map(
      (item) => item.jobId
    );

    const newlySelected = currentSelection.filter(
      (node) => !previousSelectionIds.includes(node.jobId)
    );
    const newlyDeselected = this.previousSelection.filter(
      (node) => !currentSelectionIds.includes(node.jobId)
    );

    // Handling changes based on newly selected or deselected
    newlySelected.forEach((node) => {
      const syntheticEvent = {
        target: {
          name: 'chk_' + node.jobNum,
          checked: true,
        },
      };
      this.props.handleCheckChange(syntheticEvent);
    });

    newlyDeselected.forEach((node) => {
      const syntheticEvent = {
        target: {
          name: 'chk_' + node.jobNum,
          checked: false,
        },
      };
      this.props.handleCheckChange(syntheticEvent);
    });

    // Update the previous selection with current
    this.previousSelection = [...currentSelection];
  };

  onColumnResized = (params) => {
    if (params.finished) {
      // Update the state with new column width
      const newWidths = { ...this.state.columnWidths };
      params.columns.forEach((column) => {
        newWidths[column.getColId()] = column.getActualWidth();
      });
      this.setState({ columnWidths: newWidths });
    }
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    if (this.props.onGridReady) {
      this.props.onGridReady(params);
    }
    this.adjustHeaderHeight(params.api);
    this.autoSizeUnspecifiedColumns();
  };

  onDataLoaded = (newData) => {
    this.gridApi.setRowData(newData);
    this.autoSizeUnspecifiedColumns(true);
  };

  onSortChanged = () => {
    if (!this.gridApi || typeof this.props.onSortedData !== 'function') {
      return;
    }

    const rowCount = this.gridApi.getDisplayedRowCount();
    const sortedRows = [];
    for (let i = 0; i < rowCount; i += 1) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
      if (rowNode && rowNode.data) {
        sortedRows.push(rowNode.data);
      }
    }

    this.props.onSortedData(sortedRows);
  };

  render() {
    const gridOptions = {
      suppressSizeToFit: true,
      columnDefs: columns,
      rowData: Array.isArray(this.props.workOrders)
        ? this.props.workOrders
        : [],
      context: {
        componentParent: this,
        handleCheckChange: this.props.handleCheckChange,
        gridApi: this.gridApi,
        addSchedule: this.props.addSchedule,
        setModal: this.props.setModal,
        setInterpreterEmail: this.props.setInterpreterEmail,
        onChange: this.props.handleCheckChange,
        state: this.props.state,
      },
      animateRows: true,
      defaultColDef: defaultColDef,
      components: {
        jobNumCellRenderer: CellRenderers.JobNumCellRenderer, // Register the cell renderer
        auditFlgCellRenderer: CellRenderers.AuditFlgCellRenderer,
        transportationCellRenderer: CellRenderers.TransportationCellRenderer,
        durationCellRenderer: CellRenderers.DurationCellRenderer,
        repEmail2CellRenderer: CellRenderers.RepEmail2CellRenderer,
        jobDateCellRenderer: CellRenderers.JobDateCellRenderer,
        claimantNameCellRenderer: CellRenderers.ClaimantNameCellRenderer,
        intNameCellRenderer: CellRenderers.IntNameCellRenderer,
        repNameCellRenderer: CellRenderers.RepNameCellRenderer,
      },
      rowSelection: 'multiple',
      onRowClicked: this.onRowClicked,
      onColumnResized: this.onColumnResized,
      onSortChanged: this.onSortChanged,
      rowHeight: 20,
      tooltipShowDelay: 0,
      enableBrowserTooltips: true,
      onRowDoubleClicked: this.handleRowDoubleClick,
      onSelectionChanged: this.onSelectionChanged,
    };

    return (
      <div
        className="ag-theme-balham"
        style={{
          width: '100%',
          height: '600px',
        }}
      >
        <AgGridReact {...gridOptions} onGridReady={this.onGridReady} />
      </div>
    );
  }
}
